import { addQueryArgs } from "@wordpress/url";
import { pick } from "lodash";
import { getUserLocales } from 'get-user-locale';
import config from "./config";
import type { CountryData, Title, TitleIdType, TitleObjectType } from "../types";
import { convertKeys } from "./utils";

import { camelCase, snakeCase } from 'change-case';

export type FetchResult = {
  page: number;
  totalPages: number;
  totalResults: number;
  items: Title[];
};

export const LOCALE = config.locale
  || getUserLocales().find((locale) => locale.length >= 5)?.replace('-', '_')
  || 'en_US';

// Generic fetch helper
const fetchHelper = async <T>(
  url: string,
  options: { signal?: AbortSignal } = {}
): Promise<T | null> => {
  const { nonce = '' } = config || {};

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-WP-Nonce": nonce,
    },
    credentials: "include",
    ...options, // includes signal, etc.
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const text = await response.text();
  if (!text) return null;

  try {
    const result = JSON.parse(text);
    return result ? convertKeys(result, camelCase) : null;
  } catch {
    return null;
  }
};

// Fetch countries
export const fetchCountries = async (
  options: {
    apiKey?: string;
    apiUrl?: string;
    signal?: AbortSignal;
  } = {}
): Promise<CountryData[] | null> => {
  const { apiUrl = config?.settings?.apiUrl, apiKey = config?.settings?.apiKey, signal } = options;

  const url = addQueryArgs(
    `${apiUrl}/countries`,
    { token: apiKey }
  );

  return fetchHelper<CountryData[]>(url, { signal });
};

// Fetch titles by query
export const fetchTitlesByQuery = async (
  query: string,
  options: {
    objectType?: "show" | "movie" | "all";
    locale?: string;
    apiKey?: string;
    apiUrl?: string;
    page?: number;
    pageSize?: number;
    signal?: AbortSignal;
  } = {}
): Promise<FetchResult | null> => {
  const { apiUrl = config?.settings?.apiUrl, apiKey = config?.settings?.apiKey, objectType = "all", locale = LOCALE, signal, ...params } = options;
  const url = addQueryArgs(
    `${apiUrl}/titles/object_type/${objectType}/locale/${locale}`,
    convertKeys({ query, token: apiKey, ...pick(params, ["page", "pageSize"]) }, snakeCase)
  );

  return fetchHelper<FetchResult>(url, { signal })
};

// Fetch title by ID
export const fetchTitleById = async (
  id: string | number,
  objectType: TitleObjectType,
  options: {
    idType?: TitleIdType;
    locale?: string;
    apiKey?: string;
    apiUrl?: string;
    signal?: AbortSignal;
  } = {}
): Promise<Title | null> => {
  const { apiUrl = config?.settings?.apiUrl, apiKey = config?.settings?.apiKey, locale = LOCALE, idType = "justwatch", signal } = options;

  const url = addQueryArgs(
    `${apiUrl}/offers/object_type/${objectType}/id_type/${idType}/locale/${locale}`,
    { token: apiKey, id }
  );

  return fetchHelper<Title>(url, { signal });
};

// Get the list of full_locales from the fetched countries
export const getLocales = (countries: CountryData[]): string[] => {
  return countries.map((country) => country.fullLocale);
};

// Check if a string is a valid IMDb ID
export const isImdbId = (input: string): boolean => /^tt\d{7,}$/.test(input);

// Get ID data for a title
export const getIdData = (
  item: Title,
  id?: string | number,
  idType?: TitleIdType
): { id: string; idType: TitleIdType } => {
  const parsedIdType = id && isImdbId(id.toString()) ? "imdb" : idType;
  const idTypes: TitleIdType[] = parsedIdType ? [parsedIdType] : ["imdb", "justwatch", "tmdb"];
  const allIdData = idTypes.map((type) => ({
    id: getId(item, type)?.toString() || "",
    idType: type as TitleIdType,
  }));

  return allIdData.find((data) => data.id === id) || allIdData[0];
};

// Get ID match for a title based on a highlight
export const getIdMatch = (
  item: Title,
  highlight: string,
  idType?: TitleIdType
): { id: string; idType: TitleIdType } | null => {
  const idTypes = idType ? [idType] : ["imdb", "justwatch", "tmdb"];
  const allIdData = idTypes.map((type) => ({
    id: getId(item, type as TitleIdType) || "",
    idType: type as TitleIdType,
  }));

  return allIdData.find((data) => data.id === highlight) || null;
};

// Get the URL for a title based on the ID type
export const getItemUrl = (item: Title, idType: TitleIdType = "justwatch"): string | null => ({
  imdb: item.imdbId && `https://www.imdb.com/title/${item.imdbId}/?ref_=helpms_ih_tm_listlink`,
  justwatch: item.fullPath && `https://www.justwatch.com${item.fullPath}`,
  tmdb: `https://www.themoviedb.org/movie/${item.tmdbId}`,
}[idType] || null);

// Helper to get an ID from a title based on the ID type
export const getId = (data: Title, idType: TitleIdType): string | undefined =>
  data[`${idType}Id`]?.toString();